import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { RowSelectedEvent, SelectionChangedEvent } from 'ag-grid-enterprise';
import { LoadingMask } from 'src/app/classes/loading-mask';
import { Site, SiteUser } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-assign-sites-to-user-modal',
  templateUrl: './assign-sites-to-user-modal.component.html',
  styleUrl: './assign-sites-to-user-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssignSitesToUserModalComponent implements OnInit {
  constructor(
    private ActiveModal: NgbActiveModal,
    private ToastService: ToastService,
    private SiteService: SiteService,
    private UtilityService: UtilityService
  ) {}

  @Input() UserId?: number;
  @Input() CompanyId?: number;

  Sites: Site[] = [];

  // Maps a Site Id to a Site User.
  SiteIdToSiteUserMap = new Map<number, SiteUser>([]);
  // Used for preventing errors related to selecting, unselecting, and re-selecting user.
  ExistingSiteUserMap = new Map<number, SiteUser>([]);

  selectedRowsPrimaryKeys: Set<string | number> = new Set();
  SuppliedQueryData?: GenericObject;

  close() {
    this.ActiveModal.dismiss();
  }

  save() {
    if (!this.UserId) {
      throw this.ToastService.addErrorToast('Error', 'Missing User Id.', {
        returnError: true
      });
    }

    this.SiteService.saveSiteUsers(
      Array.from(this.SiteIdToSiteUserMap.values()),
      { UserId: this.UserId }
    ).subscribe({
      next: () => {
        this.ToastService.addSuccessToast(
          'Success',
          'Successfully added site users.'
        );
        this.ActiveModal.close();
      }
    });
  }

  rowSelectionChanged(event: RowSelectedEvent) {
    const { data, source } = event;

    if (source !== 'uiSelectAll' && source !== 'api') {
      const { id: SiteId } = data;

      if (!SiteId) {
        throw this.ToastService.addErrorToast('Error', 'Site is missing id.', {
          returnError: true
        });
      }

      if (this.SiteIdToSiteUserMap.has(SiteId)) {
        this.SiteIdToSiteUserMap.delete(SiteId);
      } else if (this.ExistingSiteUserMap.has(SiteId)) {
        this.SiteIdToSiteUserMap.set(
          SiteId,
          this.ExistingSiteUserMap.get(SiteId)!
        );
      } else {
        this.SiteIdToSiteUserMap.set(
          SiteId,
          new SiteUser({ UserId: this.UserId, SiteId })
        );
      }
    }
  }

  // Used for handling select all functionality.
  siteSelectionChanged(event: SelectionChangedEvent) {
    const { source } = event;

    if (source === 'uiSelectAll') {
      // If all of the sites have already been assigned to the user, deselect them all.
      // Otherwise, add the remaining sites.
      if (this.Sites.length === this.SiteIdToSiteUserMap.size) {
        this.SiteIdToSiteUserMap.clear();
      } else {
        this.Sites.forEach((Site) => {
          const { id: SiteId } = Site;

          if (!SiteId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Site is missing id.',
              { returnError: true }
            );
          }

          if (this.ExistingSiteUserMap.has(SiteId)) {
            this.SiteIdToSiteUserMap.set(
              SiteId,
              this.ExistingSiteUserMap.get(SiteId)!
            );
          } else {
            this.SiteIdToSiteUserMap.set(
              SiteId,
              new SiteUser({ UserId: this.UserId, SiteId })
            );
          }
        });
      }
    }
  }

  ngOnInit(): void {
    if (!this.UserId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot load site. No user ID found.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot load site. No company ID found.',
        { returnError: true }
      );
    }

    this.SuppliedQueryData = {
      attributes: ['siteKey', 'ein'],
      CompanyId: this.CompanyId
    };

    this.SiteService.getSites(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['id', 'CompanyId'],
        CompanyId: this.CompanyId,
        SiteUser: {
          options: { required: false },
          UserId: this.UserId
        }
      })
    ).subscribe({
      next: (Sites) => {
        Sites.forEach((Site) => {
          const { id: SiteId, SiteUsers } = Site;

          if (!SiteId) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Site is missing id.',
              { returnError: true }
            );
          }

          if (SiteUsers && SiteUsers.length > 0) {
            if (SiteUsers.length !== 1) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Multiple site users loaded for site.',
                { returnError: true }
              );
            }

            const SiteUser = SiteUsers[0];

            this.selectedRowsPrimaryKeys.add(SiteId);
            this.SiteIdToSiteUserMap.set(SiteId, SiteUser);
            this.ExistingSiteUserMap.set(SiteId, SiteUser);
          }

          this.Sites.push(Site);
        });
      }
    });
  }
}
